import React, { useState } from 'react';
import { toast } from 'react-toastify';
import LoadingOverlay from '../../../components/LoadingOverlay';
import { T2 } from '../../../styles/titles';
import FormFilter from './components/filter';
import { IReprocessamentoFilterValues } from './components/filter/types';
import reprocessamentoService from './reprocessamentoService';
import {
  Captura,
  ModalidadePhoebus,
  IReprocessResult,
  JobType,
  isAcceptedResponse,
  resolveJobType,
} from './types';
import {
  parseDatetimeLocal,
  formatDateToKaptorIso,
  formatDateToKaptorPix,
} from '../../../utils/generalFormat';
import SyncResult from './components/result/SyncResult';
import PixJobSection from './components/result/PixJobSection';
import * as S from './styles';

const MAX_INTERVAL_HOURS = 24;

const Reprocessamento: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [syncResult, setSyncResult] = useState<IReprocessResult | null>(null);
  const [pixJobId, setPixJobId] = useState<string | null>(null);
  const [pixMessage, setPixMessage] = useState<string | null>(null);
  // Job assíncrono (202) do fluxo de crédito/débito (Aditum ou Phoebus).
  // Ambos consultam o mesmo endpoint de status (v1/transaction/...).
  const [txnJobId, setTxnJobId] = useState<string | null>(null);
  const [txnMessage, setTxnMessage] = useState<string | null>(null);
  // Captura/Modalidade atualmente selecionadas no filtro (mesmos defaults do
  // formulário). É delas que sai o jobType da consulta por JobId, para que a
  // consulta funcione mesmo com um JobId gerado por outra pessoa.
  const [captura, setCaptura] = useState<Captura>(Captura.Phoebus);
  const [modalidade, setModalidade] = useState<ModalidadePhoebus>(ModalidadePhoebus.DebitoCredito);

  const consultaJobType = resolveJobType(captura, modalidade);

  const handleCapturaModalidadeChange = (
    novaCaptura: Captura,
    novaModalidade: ModalidadePhoebus,
  ) => {
    setCaptura(novaCaptura);
    setModalidade(novaModalidade);
  };

  const isPixFlow = (values: IReprocessamentoFilterValues): boolean =>
    values.captura === Captura.Phoebus && values.modalidade === ModalidadePhoebus.Pix;

  const validateDates = (values: IReprocessamentoFilterValues): { start: Date; finish: Date } | null => {
    if (!values.startDate || !values.finishDate) {
      toast.error('Data início e data fim são obrigatórias.');
      return null;
    }

    const start = parseDatetimeLocal(values.startDate);
    const finish = parseDatetimeLocal(values.finishDate);

    if (finish <= start) {
      toast.error('A data fim deve ser posterior à data início.');
      return null;
    }

    const diffHours = (finish.getTime() - start.getTime()) / (1000 * 60 * 60);
    if (diffHours > MAX_INTERVAL_HOURS) {
      toast.error('O intervalo entre as datas não pode ultrapassar 24 horas.');
      return null;
    }

    return { start, finish };
  };

  const handlePixFlow = async (
    values: IReprocessamentoFilterValues,
    start: Date,
    finish: Date,
  ) => {
    if (!values.merchant && !values.endToEndId && !values.appPixId) {
      toast.error('Informe pelo menos o Cód. Lojista, o Identificador Pix ou o Identificador do App Pix.');
      return;
    }

    setLoading(true);
    try {
      const outcome = await reprocessamentoService.reprocessarPix({
        initDate: formatDateToKaptorPix(start),
        finishDate: formatDateToKaptorPix(finish),
        merchantId: values.merchant || null,
        appPixId: values.appPixId || null,
        endToEndId: values.endToEndId || null,
      });

      setPixJobId(outcome.data.jobId);
      setPixMessage(outcome.data.message);

      if (outcome.alreadyRunning) {
        toast.warning('Já existe um reprocessamento Pix em andamento.');
      } else {
        toast.success('Reprocessamento Pix iniciado com sucesso!');
      }
    } catch {
      toast.error('Erro ao iniciar o reprocessamento Pix. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleTransactionFlow = async (
    values: IReprocessamentoFilterValues,
    start: Date,
    finish: Date,
  ) => {
    if (!values.merchant && !values.nsu) {
      toast.error('Informe pelo menos o Cód. Lojista ou o NSU.');
      return;
    }

    setLoading(true);
    try {
      const response = await reprocessamentoService.reprocessarTransacao({
        capture: values.captura,
        startDate: formatDateToKaptorIso(start),
        endDate: formatDateToKaptorIso(finish),
        merchant: values.merchant || null,
        acquirerNsu: values.nsu || null,
      });

      if (isAcceptedResponse(response)) {
        // Resposta assíncrona (202): exibe o JobId, igual ao fluxo Pix.
        setTxnJobId(response.jobId);
        setTxnMessage(response.message);
        toast.success('Reprocessamento iniciado com sucesso!');
      } else {
        // Resposta síncrona (legado): exibe o resumo do resultado.
        setSyncResult(response);
        toast.success('Reprocessamento realizado com sucesso!');
      }
    } catch {
      toast.error('Erro ao realizar reprocessamento. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = async (values: IReprocessamentoFilterValues) => {
    // Limpa resultados anteriores a cada nova submissão.
    setSyncResult(null);
    setPixJobId(null);
    setPixMessage(null);
    setTxnJobId(null);
    setTxnMessage(null);

    const dates = validateDates(values);
    if (!dates) return;

    if (isPixFlow(values)) {
      await handlePixFlow(values, dates.start, dates.finish);
    } else {
      await handleTransactionFlow(values, dates.start, dates.finish);
    }
  };

  return (
    <S.Container>
      <LoadingOverlay show={loading} relative />
      <T2>Reprocessamento de Transações</T2>
      <FormFilter
        onFormSubmit={handleFilter}
        onCapturaModalidadeChange={handleCapturaModalidadeChange}
      />

      {syncResult && <SyncResult result={syncResult} />}

      {/*
        Um único bloco de Job/consulta. Como o formulário dispara apenas um
        fluxo por vez (Pix OU crédito/débito), no máximo um dos jobs está
        ativo. O jobType da consulta vem da Captura/Modalidade selecionadas
        no filtro (consultaJobType), não de quem disparou — assim a consulta
        por JobId funciona com um id gerado por outra pessoa.
      */}
      <PixJobSection
        startedJobId={pixJobId ?? txnJobId}
        startedMessage={pixMessage ?? txnMessage}
        jobType={consultaJobType}
        startedTitle={
          consultaJobType === JobType.Pix
            ? 'Reprocessamento Pix iniciado'
            : 'Reprocessamento iniciado'
        }
      />
    </S.Container>
  );
};

export default Reprocessamento;
